import {
    useCallback, useContext, useRef, useState,
} from 'react';

import Typography from '@mui/material/Typography';
import MuiTextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';

import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';

import {
    Formik, Form, Field, useField,
} from 'formik';
import styled from 'styled-components';
import { UserActions } from 'BMapsCmds';
import { App } from '../BMapsApp';
import { EventBroker } from '../eventbroker';
import { takeLastSelectResult, selectResultHint } from '../project_data';
import ProteinContextProvider, { ProteinContext } from './common/ProteinContextProvider';
import ProteinSelect from './common/ProteinSelect';
import {
    useEventBrokerStateHandling, useCloseOnEscape,
} from './common/helpers/useEventBrokerSubscription';
import useInputHistory from './common/helpers/useInputHistory';
import { createInputHistory } from './common/inputHistory';
import { withColorSchemeInfoStyled, useColorSchemeInfo } from '../redux/prefs/access';

// Queries submitted from the select box, for up/down recall. One history for all proteins.
// It lives here rather than in a component because SelectBoxForm unmounts on every close.
// Queries run by other callers of UserActions.Select (command panel, Gibbs) are not recorded.
// Kept in localStorage so recall survives a reload; it also survives a logout.
export const selectQueryHistory = createInputHistory({
    storageKey: 'bmaps_select_query_history_v1',
    storage: 'localStorage',
});

// Color the query text field against the (often dark) overlay background.
const StyledTextField = withColorSchemeInfoStyled(styled(MuiTextField)`
  ${({ $colorSchemeInfo: { textCss: color } }) => `
        & label {
            color: ${color};
            &.Mui-focused {
                color: ${color};
            }
        }
        & input {
            color: ${color};
        }
        & input::placeholder {
            color: ${color};
            font-style: italic;
            opacity: 0.6;
        }
        & .MuiInput-underline:after {
            border-bottom-color: ${color};
        }
        & .MuiOutlinedInput-root {
            & fieldset {
                border-color: ${color};
            }
            &:hover fieldset {
                border-color: ${color};
            }
            &.Mui-focused fieldset {
                border-color: ${color};
            }
        }
    `}
`);

// Same colors as the query field, applied to the picker via MUI `sx` (emotion) so the
// label (default + focused), the selected value text, the dropdown arrow, and the outline
// all track the scheme's text color. Styling the picker through styled-components did not
// win the cascade for the InputLabel, so we use MUI's own styling API here.
const proteinSelectSx = (color) => ({
    marginBottom: '0.5em',
    '& .MuiInputLabel-root': { color },
    '& .MuiInputLabel-root.Mui-focused': { color },
    '& .MuiSelect-select': { color },
    '& .MuiSvgIcon-root': { color },
    '& .MuiOutlinedInput-root': {
        '& fieldset': { borderColor: color },
        '&:hover fieldset': { borderColor: color },
        '&.Mui-focused fieldset': { borderColor: color },
    },
});

export default function SelectBox() {
    const [open, setOpen] = useState(false);
    // Remember the picked protein across open/close (this component stays mounted; the
    // ProteinContextProvider subtree below is unmounted each time the box closes).
    const [rememberedProtein, setRememberedProtein] = useState(null);

    const getNewOpen = useCallback(
        (evtArg, oldOpen) => evtArg === true || (evtArg === 'toggle' && !oldOpen),
        []
    );
    useEventBrokerStateHandling('openSelectBox', open, setOpen, getNewOpen);
    useCloseOnEscape(setOpen);
    const { css, textCss, controlAlphaCss } = useColorSchemeInfo();

    const style = {
        position: 'absolute',
        right: 0,
        bottom: 0,
        backgroundColor: css + controlAlphaCss,
        paddingLeft: '0.5em',
        paddingTop: '0.5em',
        paddingBottom: 'var(--mol-button-group-border)',
    };

    // Restore the remembered protein only if it is still loaded; otherwise fall back to the
    // first protein (defaultToFirstProtein). This runs on every render, before the open guard,
    // and App is not initialized during SSR/prerender, so gate on App.Ready. getLoadedProteins
    // returns stable MapCase objects, so an identity check is valid.
    const loadedProteins = App.Ready ? App.Workspace.getLoadedProteins() : [];
    const initialProtein = loadedProteins.includes(rememberedProtein) ? rememberedProtein : null;

    // ProteinContextProvider supplies the loaded proteins and tracks the picked one.
    // initialProtein restores the last pick; defaultToFirstProtein resolves a target up front
    // so a query can be submitted without first interacting with the picker.
    // Gate on App.Ready: ProteinContextProvider reads App.Workspace unconditionally, which is
    // undefined before app init (e.g., if openSelectBox somehow fires early / during prerender).
    return open && App.Ready && (
        <Typography component="div" style={style}>
            <ProteinContextProvider initialProtein={initialProtein} defaultToFirstProtein>
                <SelectBoxForm
                    setOpen={setOpen}
                    textCss={textCss}
                    onProteinChange={setRememberedProtein}
                />
            </ProteinContextProvider>
        </Typography>
    );
}

function SelectBoxForm({ setOpen, textCss, onProteinChange }) {
    // selectedCaseData follows the protein picked in ProteinSelect (which writes to context).
    const { selectedCaseData, loadedProteins, lookupCaseData } = useContext(ProteinContext);

    const handleSubmit = useCallback(async ({ query: typed }) => {
        const query = typed.trim();
        // Empty input: keep the box open and send nothing. Don't rerun anything here: selection
        // isn't undoable, so an accidental Enter would replace the user's selection.
        if (!query) return;
        // Recorded before sending, so a query that fails or matches nothing is still one Up away.
        selectQueryHistory.record(query);
        setOpen(false);
        takeLastSelectResult(); // so only this query's result is read below

        // Target a specific protein only when more than one is loaded. With 0 or 1 protein,
        // App.ServerConnection is the only connection, so submit with no target to preserve
        // the existing routing exactly.
        if (loadedProteins.length > 1 && selectedCaseData) {
            // There is no UI for managing selections across multiple proteins, so a targeted
            // selection is exclusive: clear any selection on the other loaded proteins first,
            // then select on the chosen one LAST (so its solute ends active in multi-protein
            // mode). UserActions.Select(..., caseData) routes each call to the right connector
            // (selectivity) or activates that solute first (multi-protein).
            const otherCaseData = loadedProteins
                .map((protein) => lookupCaseData(protein))
                .filter((caseData) => caseData && caseData !== selectedCaseData);
            for (const caseData of otherCaseData) {
                // Sequential on purpose: in multi-protein mode these share one connector and
                // each switches the active solute, so they must not interleave.
                await UserActions.Select('none', caseData); // eslint-disable-line no-await-in-loop
            }
            await UserActions.Select(query, selectedCaseData);
        } else {
            await UserActions.Select(query);
        }

        // The status bar only counts selected atoms, so a query that matches nothing would
        // otherwise leave no trace. StatusBar shows this until the user's next input.
        // The server adds a hint for a likely misspelled keyword ("Did you mean 'protein'
        // instead of 'protien'?").
        if (App.Workspace.getSelectedAtoms().length === 0) {
            const hint = selectResultHint(takeLastSelectResult());
            EventBroker.publish('selectionNotice', hint ? `Nothing selected. ${hint}` : 'Nothing selected');
        }
    }, [loadedProteins, selectedCaseData, lookupCaseData, setOpen]);

    return (
        <Formik initialValues={{ query: '' }} onSubmit={handleSubmit}>
            <Form>
                {/* ProteinSelect renders null unless 2+ proteins are loaded, so the
                    single-/zero-protein SelectBox is unchanged. It sits on its own row
                    above the query input (the box grows upward into the canvas). */}
                <ProteinSelect
                    id="select-box_protein-select"
                    label="Structure to query"
                    sx={proteinSelectSx(textCss)}
                    getOptionLabel={(prot) => prot.getShortName()}
                    onChange={(evt, { protein } = {}) => onProteinChange(protein)}
                />
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <QueryField />
                    {/* A real submit button (not FormikIconSubmit, which is type="button")
                        so Enter submits reliably now that the form has more than one field. */}
                    <IconButton type="submit" aria-label="Run selection query">
                        <SearchIcon style={{ color: textCss }} />
                    </IconButton>
                </div>
            </Form>
        </Formik>
    );
}

/**
 * The query input, with up/down recall of earlier queries, and a button opening a menu of them
 * where each can be picked, or removed from the history.
 */
function QueryField() {
    const [{ value }, , { setValue }] = useField('query');
    const { onKeyDown, reset: resetRecall } = useInputHistory({
        getEntries: selectQueryHistory.getEntries, value, setValue,
    });
    const { textCss } = useColorSchemeInfo();
    // Read on open and after each removal from the menu. The only other change is a submit,
    // which closes the box.
    const [entries, setEntries] = useState(() => selectQueryHistory.getEntries());
    const hasHistory = entries.length > 0;
    const [menuAnchor, setMenuAnchor] = useState(null);
    const inputRef = useRef(null);

    const closeMenu = useCallback((evt, reason) => {
        if (reason === 'escapeKeyDown') swallowNextEscapeKeyUp();
        setMenuAnchor(null);
    }, []);

    // Each of these changes the history or the field outside recall, so recall starts over.
    function pick(entry) {
        resetRecall();
        setValue(entry);
        closeMenu();
    }

    function remove(entry) {
        resetRecall();
        selectQueryHistory.remove(entry);
        const remaining = selectQueryHistory.getEntries();
        setEntries(remaining);
        if (!remaining.length) closeMenu();
    }

    function removeKeepingFocus(itemEl, entry) {
        // The removed row may hold focus (its item, or its ✕). Move focus to a neighbouring entry,
        // or it drops to the body, where Escape can't reach the menu but its keyup still closes
        // the select box.
        const items = [...itemEl.parentElement.querySelectorAll('[data-history-entry]')];
        const i = items.indexOf(itemEl);
        const nextFocus = items[i + 1] || items[i - 1];
        remove(entry);
        nextFocus?.focus();
    }

    const clearAll = () => {
        resetRecall();
        selectQueryHistory.clear();
        setEntries([]);
        closeMenu();
    };

    return (
        <>
            <Field // Note: We don't need an error area, so don't use common/TextField
                as={StyledTextField}
                name="query"
                label="Select atoms query"
                placeholder={hasHistory ? '↑ for recent queries' : undefined}
                // The browser's own form history would compete with ours on the same keys.
                autoComplete="off"
                inputRef={inputRef}
                onKeyDown={onKeyDown}
                autoFocus
            />
            {hasHistory && (
                <IconButton
                    aria-label="Recent queries"
                    aria-haspopup="menu"
                    aria-expanded={Boolean(menuAnchor)}
                    onClick={(evt) => setMenuAnchor(evt.currentTarget)}
                >
                    <HistoryIcon style={{ color: textCss }} />
                </IconButton>
            )}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={closeMenu}
                // The box sits at the bottom of the canvas, so open upward.
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                // Return to the query field, not the menu button, so typing can carry on.
                disableRestoreFocus
                slotProps={{
                    paper: { sx: { maxWidth: '30em' } },
                    transition: { onExited: () => inputRef.current?.focus() },
                }}
            >
                {entries.map((entry) => (
                    <MenuItem
                        key={entry}
                        data-history-entry
                        title={entry}
                        onClick={() => pick(entry)}
                        onKeyDown={(evt) => {
                            if (evt.key !== 'Delete') return;
                            evt.preventDefault();
                            removeKeepingFocus(evt.currentTarget, entry);
                        }}
                    >
                        <ListItemText primary={entry} slotProps={{ primary: { noWrap: true } }} />
                        <IconButton
                            size="small"
                            edge="end"
                            // Out of the tab order: it sits inside a menuitem, and Delete is the
                            // keyboard route.
                            tabIndex={-1}
                            aria-label={`Remove "${entry}" from history`}
                            onClick={(evt) => {
                                evt.stopPropagation(); // don't also pick it
                                removeKeepingFocus(
                                    evt.currentTarget.closest('[data-history-entry]'),
                                    entry,
                                );
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </MenuItem>
                ))}
                <Divider />
                <MenuItem onClick={clearAll}>Clear history</MenuItem>
            </Menu>
        </>
    );
}

/**
 * The history menu closes on Escape's keydown, but BMaps closes the whole select box on Escape's
 * keyup (init_info_display's document listener, then display_mgr publishes 'escapeKey'). Stop that
 * one keyup at the document in the capture phase, before the app's listener sees it.
 */
function swallowNextEscapeKeyUp() {
    const handler = (evt) => {
        if (evt.key !== 'Escape') return;
        evt.stopPropagation();
        stop();
    };
    // Don't outlive the key press: a keyup that never comes mustn't swallow a later Escape.
    const timer = setTimeout(() => stop(), 1000);
    function stop() {
        clearTimeout(timer);
        document.removeEventListener('keyup', handler, true);
    }
    document.addEventListener('keyup', handler, true);
}
